/**
 * Generically handles various error states around a field
 * @module elements/fields/entry-error
 */

import $ from 'jquery';
import get from 'lodash/get';
import stache from '../../lib/stache';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';

export const DEFAULT_TEMPLATES = {
  requiredTemplate: 'valid entry required',
  invalidTemplate: 'enter a valid entry',
};

const DEFAULT_ERROR_LABEL_OPTIONS = {
  label: '',
  requiredAttr: 'data-validate-not-empty',
  entryErrorRequiredMessage: '',
  entryErrorInvalidMessage: '',
  requiredTemplate: '{{label}} entry required',
  invalidTemplate: 'enter a valid {{label}}',
};

const EVENT_NS = '.entry-label';

/**
 * Responsible for taking event names and appending a namespace
 * @param {Object} events - Key/value mapping of events
 * @returns {Object} Applicable events containing a namespace
 */
const appendNamespaceToEvents = events =>
  Object
    .keys(events)
    .reduce(
      (acc, name) => Object.assign({}, acc, { [name]: events[name] + EVENT_NS }),
      {},
    );

export const EVENTS = {
  INVALID: 'invalid',
  BLUR: 'blur',
  FOCUS: 'focus',
};

const { INVALID, BLUR, FOCUS } = appendNamespaceToEvents(EVENTS);

/**
 * Performs any sort of clean up
 * @param {jQuery} $el - Form field
 * @param {Function} deregister - Internally removes the plugin from the
 * registery
 */
const destroy = ($el, deregister) => () => {
  $el.off(EVENT_NS);
  deregister();
};

/**
 * Responsible creating the ability to set an error message
 * @param {jQuery} $el - Form field
 * @param {string} requiredMessage - Error message when a field is required
 * @param {string} invalidMessage - Error message when a field is invalid
 * @returns {Function} Called on to set a possible error message
 */
const makeSetMessage = ($el, requiredMessage, invalidMessage) => () => {
  const el = $el[0];

  if (!$el.val()) {
    el.setCustomValidity(requiredMessage);
    return;
  }

  if ($el.is(':invalid')) {
    el.setCustomValidity(invalidMessage);
    return;
  }

  el.setCustomValidity('');
};

/**
 * Responsible for resetting an error on a field
 * @param {HTMLElement} el - Form field
 */
const resetField = (el) => {
  el.setCustomValidity('');
};

/**
 * Responsible for applying an error if applicable
 * @param {jQuery} $el - Form field
 * @param {Function} setMessage - If applicable sets an error message
 */
const applyError = ($el, setMessage) => {
  const el = $el[0];
  const { validateOnlyOnSubmit } = get(el, 'form.dataset');
  if (validateOnlyOnSubmit) {
    return;
  }

  setMessage();
  el.checkValidity();
};

/**
 * Responsible obtaining an error message
 * @param {string} custom - A custom error message
 * @param {string} template - Template used for the error message
 * @param {Object} data - Data used for the error message template
 */
const getMessage = (custom, template, data) => custom || stache(template, data);

/**
 * Determines which template or message should be used from most specific to
 * most general based on a number of configured values.
 * @param {string|undefined} label - Name associated to field
 * @param {string|undefined} errorMessage - Message passed in on a field level
 * @param {string} errorType - Type of error
 * @returns {string} Template or message
 */
const getTemplate = (label, errorMessage, errorType) => {
  const template = DEFAULT_ERROR_LABEL_OPTIONS[errorType];
  const defaultMessage = DEFAULT_TEMPLATES[errorType];

  // This means the error message was provided on a field level, so that wins
  // over any other option.
  if (errorMessage) {
    return errorMessage;
  }

  // By default, a label is required since the default template is dependent
  // on the dynamic value of the label. There is a chance that the template
  // could still be undefined since the template can be undefined. In those
  // cases, just use the default message. This should at least indicate
  // something is broken without seeing template variables.
  if (label) {
    return template || defaultMessage;
  }

  // If no label is provided, we cannot use the template, so fallback to the
  // default message as the lowest common denominator.
  return defaultMessage;
};

/**
 * Responsible for handling errors in a standardized way. In particular, it'll
 * create consistent error handling based on the type of errors.
 * @param {HTMLElement} el - Element with error in question
 * @param {Object} options - Configurable options
 * @param {Object} actions - Actions to be called on
 * @return {Object} Methods attached to an entry error
 */
export default function entryError(el, options = {}, { deregister } = noopActions) {
  const $el = $(el);
  const {
    label,
    requiredAttr,
    requiredTemplate: requiredTemplateOption,
    invalidTemplate: invalidTemplateOption,
    entryErrorRequiredMessage: customRequiredMessage,
    entryErrorInvalidMessage: customInvalidMessage,
  } = Object.assign({}, DEFAULT_ERROR_LABEL_OPTIONS, options, el.dataset);

  const requiredTemplate = getTemplate(label, requiredTemplateOption, 'requiredTemplate');
  const invalidTemplate = getTemplate(label, invalidTemplateOption, 'invalidTemplate');
  const requiredMessage = getMessage(customRequiredMessage, requiredTemplate, { label });
  const invalidMessage = getMessage(customInvalidMessage, invalidTemplate, { label });
  const setMessage = makeSetMessage($el, requiredMessage, invalidMessage);

  $el
    .attr(requiredAttr, true)
    .on(FOCUS, () => resetField(el))
    .on(BLUR, () => applyError($el, setMessage))
    .on(INVALID, setMessage);

  return {
    destroy: destroy($el, deregister),
  };
}

registerJQueryPlugin('entryError', entryError);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/entry-error.js